import React, { useState, useEffect } from 'react';
import axios from '../../api/axios';
import { getAllTemplatesCategoriesRoute } from '../../api/routes';
import LottieIcon from '../../components/common/LottieIcon';
import { getIconCategoryClass } from '../../components/common/iconCategory';
import toDisplayAssetUrl from '../../helpers/DisplayAssetUrl';
import { getTemplateCategoryIds, templateHasCategory } from '../../helpers/templateCategories';

/**
 * A two-step picker: category → template.
 * @param {function} onSelect(templateId)  - called when user picks a template
 * @param {function} [onBack]              - called when user wants to leave (omit when embedded in a tab)
 * @param {boolean}  [embedded]            - when true, hides the top-level back button
 * @param {string}   [backLabel]           - override the back button label
 * @param {function} [onDragStart]         - called when a template card drag starts; receives a drag-item object
 * @param {function} [onDragEnd]           - called when a template card drag ends
 * @param {Array}    [featuresList]        - full features list from NodeEditor for icon colour lookup
 */
const FeatureTemplatePicker = ({ onSelect, onBack, embedded = false, backLabel, onDragStart, onDragEnd, featuresList = [] }) => {
    const [categories, setCategories] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState(null);
    const [loading, setLoading] = useState(true);

    // featuresList is not needed for icon lookup — primaryVarKey/primaryFeatureCategory
    // are resolved server-side by the categories-with-templates route.

    useEffect(() => {
        axios.get(getAllTemplatesCategoriesRoute, { withCredentials: true })
            .then(res => {
                if (res.data.success) {
                    const data = res.data.data;
                    // Only feature templates (templateType === 'feature')
                    const featureTemplates = (data.templates || []).filter(
                        t => t.templateType === 'feature' && t.primaryVarKey
                    );
                    setTemplates(featureTemplates);
                    // Only categories that contain at least one feature template
                    const populatedCatIds = new Set(featureTemplates.flatMap((template) => getTemplateCategoryIds(template)));
                    setCategories(
                        (data.categories || []).filter(c =>
                            populatedCatIds.has(c._id) || populatedCatIds.has(c.id)
                        )
                    );
                }
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    if (loading) {
        return <div className="ftp-loading">Loading templates…</div>;
    }

    // ── Category view ──────────────────────────────────────────────────────────
    if (!selectedCategoryId) {
        return (
            <div className="ftp-view">
                <div className="ftp-toolbar">
                    {!embedded && onBack && (
                        <button className="ftp-back" onClick={onBack}>{backLabel || '← Back'}</button>
                    )}
                    <span className="ftp-subtitle">Select a Template Category</span>
                </div>
                {categories.length === 0 ? (
                    <p className="ftp-empty">No feature templates are available yet.</p>
                ) : (
                    <div className="ftp-grid">
                        {categories.map(cat => (
                            <div
                                key={cat._id || cat.id}
                                className="ftp-card ftp-card--category"
                                onClick={() => setSelectedCategoryId(cat._id || cat.id)}
                            >
                                    <div className="ftp-card-img-wrap">
                                    <div
                                        className="ftp-card-img"
                                        style={
                                            cat.categoryImage
                                                ? { backgroundImage: `url(${toDisplayAssetUrl(cat.categoryImage)})` }
                                                : {}
                                        }
                                    />
                                </div>
                                <div className="ftp-card-label">{cat.name}</div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    // ── Template view ──────────────────────────────────────────────────────────
    const categoryTemplates = templates.filter((template) => templateHasCategory(template, selectedCategoryId));
    const selectedCategory = categories.find(
        c => (c._id || c.id) === selectedCategoryId
    );

    return (
        <div className="ftp-view">
            <div className="ftp-toolbar">
                <button
                    className="ftp-back"
                    onClick={() => setSelectedCategoryId(null)}
                >
                    ← {selectedCategory?.name || 'Categories'}
                </button>
                <span className="ftp-subtitle">Select a Template</span>
            </div>
            {categoryTemplates.length === 0 ? (
                <p className="ftp-empty">No templates in this category.</p>
            ) : (
                <div className="ftp-grid">
                    {categoryTemplates.map(t => {
                        const featVarKey = t.primaryVarKey || '';
                        const rawCat = Array.isArray(t.primaryFeatureCategory) ? t.primaryFeatureCategory[0] : (t.primaryFeatureCategory || 'misc');
                        const featCatSlug = getIconCategoryClass(rawCat);
                        return (
                            <div
                                key={t._id || t.id}
                                className="ftp-card"
                                draggable={!!onDragStart}
                                onClick={() => onSelect(t._id || t.id)}
                                onDragStart={onDragStart ? (e) => {
                                    e.dataTransfer.effectAllowed = 'copy';
                                    try { e.dataTransfer.setData('text/plain', t._id || t.id); } catch (_) {}
                                    onDragStart({ isTemplate: true, templateId: t._id || t.id, label: t.name });
                                } : undefined}
                                onDragEnd={onDragEnd || undefined}
                            >
                                <div className="ftp-card-img-wrap">
                                    <div
                                        className="ftp-card-img"
                                        style={
                                            t.templateImage
                                                ? { backgroundImage: `url(${toDisplayAssetUrl(t.templateImage)})` }
                                                : {}
                                        }
                                    />
                                    <div className={`ftp-icon-badge category-icon-bg--${featCatSlug}`}>
                                        <LottieIcon varKey={featVarKey} size={20} />
                                    </div>
                                </div>
                                <div className="ftp-card-label">{t.name}</div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default FeatureTemplatePicker;
